import styled from "@emotion/styled";
import { GroupAddSharp, Polyline } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyIcon from "@mui/icons-material/VpnKey";
import Tooltip from "@mui/material/Tooltip";
import { ReactComponent as AccountList } from "../../assets/icons/accountList.svg";
import { ReactComponent as Clone } from "../../assets/icons/clone.svg";
import { ReactComponent as Confirm } from "../../assets/icons/confirm.svg";
import { ReactComponent as Details } from "../../assets/icons/details.svg";
import { ReactComponent as IP } from "../../assets/icons/ip.svg";
import { ReactComponent as Pdf } from "../../assets/icons/pdf.svg";
import { ReactComponent as Play } from "../../assets/icons/play.svg";
import { IAction } from "../../types/table/interfaces.type";
import { getDisabledActionProps } from "../../utils/helpers";
import TerminalIcon from "@mui/icons-material/Terminal";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import { COLORS } from "../../assets/theme";
import HistoryIcon from "@mui/icons-material/History";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DownloadIcon from "@mui/icons-material/Download";
import RegeneratingIcon from "../../assets/icons/regenerate";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

interface ActionParams {
  id: string | number;
}
const StyledCloneIcon = styled(Clone)(
  ({ disabled }: { disabled: boolean }) => ({
    width: "24px",
    height: "24px",
    "& path[stroke]": {
      stroke: disabled ? "#D3D3D3" : "#A39455",
    },
    "& path[fill]:not([fill='none'])": {
      fill: disabled ? "#D3D3D3" : "#A39455",
    },
  })
);

const StyledAccountListIcon = styled(AccountList)(
  ({ disabled }: { disabled: boolean }) => ({
    width: "24px",
    height: "24px",
    "& path[stroke]": {
      stroke: disabled ? "#D3D3D3" : "#A39455",
    },
    "& path[fill]:not([fill='none'])": {
      fill: disabled ? "#D3D3D3" : "#A39455",
    },
  })
);

const StyledIPIcon = styled(IP)(({ disabled }: { disabled: boolean }) => ({
  color: disabled ? "#D3D3D3" : "#A39455",
  width: "24px",
  height: "24px",
  "& path": {
    stroke: disabled ? "#D3D3D3" : "#A39455",
  },
}));

const StyledPdf = styled(Pdf)(({ disabled }: { disabled: boolean }) => ({
  "& path[fill]:not([fill='none'])": {
    fill: disabled ? "#D3D3D3" : "#A39455",
  },
}));

const StyledDetails = styled(Details)(({ disabled }: { disabled: boolean }) => ({
  "& path[fill]:not([fill='none'])": {
    fill: disabled ? "#D3D3D3" : "#A39455",
  },
  "& path[stroke]": {
    stroke: disabled ? "#D3D3D3" : "#A39455",
  },
}));

interface ActionsProps {
  handleOpenEditModal?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    onContextMenu?: (event: React.MouseEvent, id: string | number) => void;
  };
  onChangePassword?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onDelete?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onParentIcon?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onViewHistory?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onListAccounts?: {
    handler: (id: string | number) => void | ((row: any) => boolean);
    isDisabled?: boolean;
  };
  onIPAction?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onCloneAction?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onTelnetAction?: {
    handler: (id: string | number) => void | ((row: any) => boolean);
    isDisabled?: boolean;
  };
  onPdf?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onPdfDetails?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onExportAttem?: {
    handler: (id: string | number) => void | ((row: any) => boolean);
    isDisabled?: boolean;
  };
  onDownload?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    icon?: (row: any) => React.ReactNode;
  };
  onDetails?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onErrors?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
    visible?: (row: any) => boolean | boolean;
  };

  onConfirm?: {
    handler: (id: string | number, row?: any) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    icon?: (row: any) => React.ReactNode;
  };
  onPlay?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    icon?: (row: any) => React.ReactNode;
  };
  onPolyline?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  reGenerate?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onView?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onPcapTrace?: {
    handler: (row: any) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onTest?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onDiagnostics?: {
    handler: (id: string | number, row?: any) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onCompareRelated?: {
    handler: (row: any) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    tooltip?: string;
  };
}

const withMinWait = (
  handler: (params: ActionParams) => void,
  delayTime = 500
) => {
  return async (params: ActionParams): Promise<void> => {
    const handlerPromise = Promise.resolve(handler(params));
    const delayPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, delayTime);
    });
    await Promise.all([handlerPromise, delayPromise]);
  };
};

const Actions = ({
  handleOpenEditModal,
  onChangePassword,
  onDelete,
  onParentIcon,
  onViewHistory,
  onTelnetAction,
  onExportAttem,
  onDownload,
  onErrors,
  onListAccounts,
  onIPAction,
  onPdfDetails,
  onCloneAction,
  onPdf,
  onDetails,
  onConfirm,
  onPlay,
  reGenerate,
  onPolyline,
  onView,
  onPcapTrace,
  onTest,
  onDiagnostics,
  onCompareRelated,
}: ActionsProps): IAction<ActionParams>[] => {
  const actions: IAction<ActionParams>[] = [
    ...(handleOpenEditModal
      ? [
          {
            id: "edit",
            label: "Edit",
            name: "Edit",
            icon: (row: any) => {
              const { color } = getDisabledActionProps(
                row,
                handleOpenEditModal
              );
              return (
                <Tooltip title="Edit">
                  <EditIcon sx={{ color, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              handleOpenEditModal.handler(id);
            },
            onContextMenu: handleOpenEditModal.onContextMenu 
              ? (event: React.MouseEvent, row: any) => {
                  handleOpenEditModal.onContextMenu!(event, row.id);
                }
              : undefined,
            disabled: (row: any) =>
              getDisabledActionProps(row, handleOpenEditModal).disabled,
          },
        ]
      : []),
    ...(onChangePassword
      ? [
          {
            id: "changePassword",
            label: "Change Password",
            name: "Change Password",
            icon: (row: any) => {
              const { color } = getDisabledActionProps(row, onChangePassword);
              return (
                <Tooltip title="Reset Password">
                  <KeyIcon sx={{ color, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onChangePassword.handler(id);
            },
          },
        ]
      : []),
    ...(onDelete
      ? [
          {
            id: "delete",
            label: "Delete",
            name: "Delete",
            icon: (
              <Tooltip title="Delete">
                <DeleteIcon
                  sx={{
                    color: onDelete.isDisabled ? "#D3D3D3" : "#A39455",
                    width: 23,
                  }}
                />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              onDelete.handler(id);
            },
            disabled: onDelete.isDisabled || false,
          },
        ]
      : []),
    ...(onListAccounts
      ? [
          {
            id: "listAccounts",
            label: "List of Accounts",
            name: "List Accounts",
            icon: (row: any) => {
              const { disabled } = getDisabledActionProps(row, onListAccounts);
              return (
                <Tooltip title="List of Accounts">
                  <StyledAccountListIcon disabled={disabled} width={23} />
                </Tooltip>
              );
            },
            handler: (rowOrId: any) => {
              if (rowOrId) {
                onListAccounts.handler(rowOrId);
              } else {
                console.warn("Row is missing for List of Accounts action");
              }
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onListAccounts).disabled,
          },
        ]
      : []),
    ...(onIPAction
      ? [
          {
            id: "ipAction",
            label: "IP Action",
            name: "IP",
            icon: (row: any) => {
              const { disabled } = getDisabledActionProps(row, onIPAction);
              return (
              <Tooltip title="IP Action">
                <StyledIPIcon disabled={disabled} />
                  </Tooltip>
                );
              },
            handler: ({ id }: ActionParams) => {
              if (id) {
                onIPAction.handler(id);
              } else {
                console.warn("Identifier is missing for IP Action");
              }
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onIPAction).disabled,
          },
        ]
      : []),
    ...(onCloneAction
      ? [
          {
            id: "cloneAction",
            label: "Clone",
            name: "Clone",
            icon: (row: any) => {
              const { disabled } = getDisabledActionProps(row, onCloneAction);
              return (
              <Tooltip title="Clone">
                <StyledCloneIcon disabled={disabled} />
                  </Tooltip>
                );
              },
            handler: ({ id }: ActionParams) => {
              if (id) {
                onCloneAction.handler(id);
              } else {
                console.warn("Identifier is missing for Clone Action");
              }
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onCloneAction).disabled,
          },
        ]
      : []),
    ...(onTelnetAction
      ? [
          {
            id: "Telnet",
            label: "Telnet",
            name: "Telnet",
            icon: (
              <Tooltip title="logs">
                <TerminalIcon
                  sx={{
                    fontSize: 23,
                    color: onTelnetAction.isDisabled
                      ? "action.disabled"
                      : COLORS.GOLD,
                  }}
                />
              </Tooltip>
            ),
            handler: (rowOrId: any) => {
              if (rowOrId) {
                onTelnetAction.handler(rowOrId);
              } else {
                console.warn("Row is missing for List of Accounts action");
              }
            },
            disabled: onTelnetAction.isDisabled || false,
          },
        ]
      : []),
    ...(onPcapTrace
      ? [
          {
            id: "pcapTrace",
            label: "PCAP Trace",
            name: "PCAP Trace",
            icon: (row: any) => {
              const isDisabled =
                typeof onPcapTrace.isDisabled === "function"
                  ? onPcapTrace.isDisabled(row)
                  : onPcapTrace.isDisabled;
              return (
                <Tooltip title="PCAP Trace">
                  <NetworkCheckIcon
                    sx={{
                      fontSize: 23,
                      color: isDisabled ? "action.disabled" : COLORS.GOLD,
                    }}
                  />
                </Tooltip>
              );
            },
            handler: (rowOrId: any) => {
              if (rowOrId) {
                onPcapTrace.handler(rowOrId);
              } else {
                console.warn("Row is missing for PCAP Trace action");
              }
            },
            disabled: onPcapTrace.isDisabled || false,
          },
        ]
      : []),
    ...(onParentIcon
      ? [
          {
            id: "parentIcon",
            label: "Parent Icon",
            name: "Parent",
            icon: (
              <Tooltip title="Parent Icon">
                <GroupAddSharp
                  sx={{
                    color: onParentIcon.isDisabled ? "#D3D3D3" : "#A39455",
                  }}
                />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              onParentIcon.handler(id);
            },
            disabled: onParentIcon.isDisabled || false,
          },
        ]
      : []),
    ...(onViewHistory
      ? [
          {
            id: "history",
            label: "View History",
            name: "History",
            icon: (
              <Tooltip title="View History">
                <HistoryIcon
                  sx={{
                    color: onViewHistory.isDisabled ? "#D3D3D3" : "#A39455",
                    width: 23,
                  }}
                />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              const identifier = id;
              if (identifier && !onViewHistory.isDisabled) {
                onViewHistory.handler(identifier);
              }
            },
            disabled: onViewHistory.isDisabled || false,
          },
        ]
      : []),
    ...(onPdf
      ? [
          {
            id: "pdf",
            label: "",
            name: "pdf",
            icon: (
              <Tooltip title="PDF">
                <StyledPdf width={23} disabled={onPdf.isDisabled || false} />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              const identifier = id;
              if (identifier && !onPdf.isDisabled) {
                onPdf.handler(identifier);
              }
            },
            disabled: onPdf.isDisabled || false,
          },
        ]
      : []),
    ...(onPdfDetails
      ? [
          {
            id: "PdfDetails",
            label: "",
            name: "PdfDetails",
            icon: (
              <Tooltip title="Pdf Details">
                <Pdf width={23} />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              const identifier = id;
              if (identifier && !onPdfDetails.isDisabled) {
                onPdfDetails.handler(identifier);
              }
            },
            disabled: onPdfDetails.isDisabled || false,
          },
        ]
      : []),
    ...(onExportAttem
      ? [
          {
            id: "Attem",
            label: "",
            name: "Attem",
            icon: (row: any) => {
              const stroke = getDisabledActionProps(row, onExportAttem).stroke;
              return (
                <Tooltip title="Download file">
                  <FileDownloadIcon
                    sx={{ color: stroke, width: 23, height: 30 }}
                  />
                </Tooltip>
              );
            },
            handler: (rowOrId: any) => {
              if (rowOrId) {
                onExportAttem.handler(rowOrId);
              } else {
                console.warn("Row is missing for List of Accounts action");
              }
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onExportAttem).disabled,
          },
        ]
      : []),
    ...(onDownload
      ? [
          {
            id: "download",
            label: "Download",
            name: "Download",
            icon: (row: any) => {
              if (onDownload.icon) {
                return onDownload.icon(row);
              }
              const { color } = getDisabledActionProps(row, onDownload);
              return (
                <Tooltip title="Download PCAP file">
                  <DownloadIcon sx={{ color, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onDownload.handler(id);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onDownload).disabled,
          },
        ]
      : []),
    ...(onDetails
      ? [
          {
            id: "details",
            label: "",
            name: "details",
            icon: (
              <Tooltip title="Details">
                <StyledDetails width={23} disabled={onDetails.isDisabled || false} />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              const identifier = id;
              if (identifier && !onDetails.isDisabled) {
                onDetails.handler(identifier);
              }
            },
            disabled: onDetails.isDisabled || false,
          },
        ]
      : []),
    ...(onErrors
      ? [
          {
            id: "error",
            label: "",
            name: "error",
            icon: (row: any) => {
              const isDisabled =
                onErrors.isDisabled || !onErrors.visible?.(row);
              return (
                <Tooltip title="Download Errors">
                  <DownloadIcon
                    sx={{
                      color: isDisabled ? "#D3D3D3" : "red",
                      width: 23,
                      height: 23,
                    }}
                  />
                </Tooltip>
              );
            },
            handler: (row: any) => {
              onErrors.handler(row);
            },
            disabled: (row: any) =>
              onErrors.isDisabled || !onErrors.visible?.(row),
          },
        ]
      : []),

    ...(onConfirm
      ? [
          {
            id: "confirm",
            label: "View confirm",
            name: "confirm",
            icon: (row: any) => {
              if (onConfirm.icon) {
                return onConfirm.icon(row);
              }
              const stroke = getDisabledActionProps(row, onConfirm).stroke;
              return (
                <Tooltip title="Confirm">
                  <Confirm stroke={stroke} width="23" height="30" />
                </Tooltip>
              );
            },
            handler: (rowOrParams: any) => {
              const id = rowOrParams?.id ?? rowOrParams;
              onConfirm.handler(id, rowOrParams);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onConfirm).disabled,
          },
        ]
      : []),
    ...(onPlay
      ? [
          {
            id: "play",
            label: "execute",
            name: "play",
            icon: (row: any) => {
              if (onPlay.icon) {
                return onPlay.icon(row);
              }
              const { stroke } = getDisabledActionProps(row, onPlay);
              return (
                <Tooltip title="execute">
                  <Play style={{ marginTop: 3, color: stroke, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onPlay.handler(id);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onPlay).disabled,
          },
        ]
      : []),
    ...(onPolyline
      ? [
          {
            id: "play",
            label: "View play",
            name: "play",
            icon: (row: any) => {
              const { stroke } = getDisabledActionProps(row, onPolyline);
              return (
                <Tooltip title="View play">
                  <Polyline
                    style={{ marginTop: 3, color: stroke, width: 23 }}
                  />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onPolyline.handler(id);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onPolyline).disabled,
          },
        ]
      : []),
    ...(reGenerate
      ? [
          {
            id: "reGenerate",
            label: "reGenerate",
            name: "play",
            icon: (row: any) => {
              const isDisabled =
                typeof reGenerate.isDisabled === "function"
                  ? reGenerate.isDisabled(row)
                  : !!reGenerate.isDisabled;
              return (
                <Tooltip title="Re Generate">
                  <RegeneratingIcon disabled={isDisabled} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              reGenerate.handler(id);
            },
            disabled: reGenerate.isDisabled || false,
          },
        ]
      : []),
    ...(onView
      ? [
          {
            id: "view",
            label: "View",
            name: "view",
            icon: (row: any) => {
              const { stroke } = getDisabledActionProps(row, onView);
              return (
                <Tooltip title="View">
                  <VisibilityIcon sx={{ color: stroke, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onView.handler(id);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onView).disabled,
          },
        ]
      : []),
    ...(onTest
      ? [
          {
            id: "test",
            label: "Test API",
            name: "test",
            icon: (row: any) => {
              const { stroke } = getDisabledActionProps(row, onTest);
              return (
                <Tooltip title="Test API">
                  <PlayArrowIcon sx={{ color: stroke, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onTest.handler(id);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onTest).disabled,
          },
        ]
      : []),
    ...(onDiagnostics
      ? [
          {
            id: "diagnostics",
            label: "Diagnostics",
            name: "diagnostics",
            icon: (row: any) => {
              const isDisabled =
                typeof onDiagnostics.isDisabled === "function"
                  ? onDiagnostics.isDisabled(row)
                  : !!onDiagnostics.isDisabled;
              return (
                <Tooltip title="Diagnostics">
                  <TroubleshootIcon
                    sx={{
                      fontSize: 23,
                      color: isDisabled ? "action.disabled" : COLORS.GOLD,
                    }}
                  />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onDiagnostics.handler(id);
            },
            disabled: (row: any) =>
              typeof onDiagnostics.isDisabled === "function"
                ? onDiagnostics.isDisabled(row)
                : !!onDiagnostics.isDisabled,
          },
        ]
      : []),
    ...(onCompareRelated
      ? [
          {
            id: "compareRelated",
            label: onCompareRelated.tooltip ?? "Same Route Instructions",
            name: "compareRelated",
            icon: (row: any) => {
              const { stroke } = getDisabledActionProps(row, onCompareRelated);
              return (
                <Tooltip
                  title={onCompareRelated.tooltip ?? "Same Route Instructions"}
                >
                  <Polyline style={{ marginTop: 3, color: stroke, width: 23 }} />
                </Tooltip>
              );
            },
            handler: (row: any) => {
              onCompareRelated.handler(row);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onCompareRelated).disabled,
          },
        ]
      : []),
  ];

  const delayedActions = actions.map((action) => ({
    ...action,
    handler: withMinWait(action.handler, 500),
  }));

  return delayedActions;
};

export default Actions;
